import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useState } from 'react'
import Timer from 'react-compound-timer/build'

import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { Loader, useCustomT } from '@flowwai-frontend/shared/common-components'

import { theme } from '../../theme'
import { DEFAULT_OTP_NUM_INPUTS } from '../../constants'

import OTPBoxesField from './components/OTPBoxesField/OTPBoxesField'

import * as Styles from './styles'
import './index.css'

interface Props {
   otpValue: string
   otpLabel: string
   setOTPValue: (value: string) => void
   handleResend: () => void
   errorMessage?: string
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
   timeDurationInSecs: number
   isResendOtpLoading?: boolean
   hideSeparator?: boolean
   isResendDisabled?: boolean
   isOTPInputDisabled?: boolean
}

const OTPInputField = (props: Props): React.ReactElement => {
   const {
      otpValue,
      otpLabel,
      setOTPValue,
      handleResend,
      errorMessage,
      timerRef,
      timeDurationInSecs,
      isResendOtpLoading,
      isResendDisabled = false,
      isOTPInputDisabled = false
   } = props

   const t = useCustomT('common:otpVerification')

   const { otpVerification } = theme

   const [isTimeOut, setIsTimeOut] = useState(false)

   const shouldShowTimer = !isTimeOut && !isResendOtpLoading

   const toggleOtpTimeout = (): void => {
      setIsTimeOut(!isTimeOut)
   }

   const onClickResend = (reset: () => void): void => {
      handleResend()
      toggleOtpTimeout()
      reset()
   }

   const onResend = (reset: () => void): void => {
      if (isTimeOut && !isResendDisabled) onClickResend(reset)
   }

   const renderResendOTPButton = (reset: () => void): React.ReactElement => {
      const isResendActive = isTimeOut && !isResendDisabled

      return isResendOtpLoading ? (
         <Loader className={cn(Styles.loaderStyles)} width={16} height={16} />
      ) : (
         <div className={cn(Styles.resendOTPAndCallContainerStyles)}>
            <Button
               hierarchy={Hierarchy.Link}
               type='button'
               size='Small'
               subVariant={SubVariant.Primary}
               isDisabled={isResendDisabled}
               className={cn(
                  Styles.resendOTPTextContainerStyles,
                  isResendActive ? 'cursor-pointer' : 'cursor-not-allowed'
               )}
               onClick={() => onResend(reset)}
            >
               <span
                  className={cn(
                     Styles.resendOtpTextStyles,
                     isResendActive
                        ? otpVerification.resendOTPActiveTextColor
                        : otpVerification.resendOTPDisabledTextColor
                  )}
               >
                  {t('resend')}
               </span>
            </Button>
         </div>
      )
   }

   return (
      <Timer
         initialTime={timeDurationInSecs * 1000}
         direction='backward'
         formatValue={value => value.toString().padStart(2, '0')}
         checkpoints={[
            {
               time: 0,
               callback: () => toggleOtpTimeout()
            }
         ]}
      >
         {({ reset, start }: { reset: () => void; start: () => void }) => {
            timerRef.current = { start }
            return (
               <div className={cn(Styles.otpFieldContainerStyles)}>
                  <div className={cn(Styles.otpBoxesFieldContainerStyles)}>
                     <OTPBoxesField
                        errorMsg={errorMessage ?? undefined}
                        numInputs={DEFAULT_OTP_NUM_INPUTS}
                        disabled={isOTPInputDisabled}
                        inputClassName={cn(
                           Styles.otpInputStyles,
                           otpVerification.otpInputBGColor,
                           otpVerification.otpInputDisabledBorderColor,
                           otpVerification.otpInputTextColor
                        )}
                        inputFocusClassName={cn(
                           Styles.otpInputFocusStyles,
                           otpVerification.otpInputFocusBGColor,
                           otpVerification.otpInputFocusBorderColor
                        )}
                        errorClassName={cn(
                           Styles.otpErrorStyles,
                           otpVerification.otpInputErrorBorderColor,
                           otpVerification.otpErrorInputTextColor
                        )}
                        label={otpLabel}
                        containerStyle={'container-styles'}
                        otpValue={otpValue}
                        setOTPValue={setOTPValue}
                        hideSeparator={props.hideSeparator}
                     />
                  </div>
                  <div
                     className={cn(Styles.resendOTPWithOTPTimerContainerStyles)}
                  >
                     {renderResendOTPButton(reset)}
                     {shouldShowTimer ? (
                        <span
                           className={cn(
                              Styles.otpTimerTextStyles,
                              otpVerification.otpTimerTextColor
                           )}
                        >
                           <Timer.Minutes />
                           :<Timer.Seconds />
                        </span>
                     ) : (
                        <span
                           className={cn(
                              Styles.otpTimerTextStyles,
                              otpVerification.disabledOtpTimerTextColor
                           )}
                        >
                           {t('timerFinalState')}
                        </span>
                     )}
                  </div>
               </div>
            )
         }}
      </Timer>
   )
}

export default observer(OTPInputField)
